import { RESUME_UPLOAD_CONFIG } from './config';

export const getResumeFileKey = (file) => [
  file.name || '',
  file.size || 0,
  file.lastModified || 0,
].join('::');

export const isAcceptedResumeFile = (file) => {
  if (!file) return false;
  if (RESUME_UPLOAD_CONFIG.ACCEPTED_TYPES[file.type]) return true;
  const parts = String(file.name || '').split('.');
  const ext = parts.length > 1 ? `.${parts.pop().toLowerCase()}` : '';
  return RESUME_UPLOAD_CONFIG.ACCEPTED_EXTENSIONS.includes(ext);
};

export const mergeUniqueResumeFiles = (existingFiles = [], incomingFiles = []) => {
  const seen = new Set(existingFiles.map(getResumeFileKey));
  const uniqueIncoming = [];
  let duplicateCount = 0;

  incomingFiles.forEach((file) => {
    const key = getResumeFileKey(file);
    if (seen.has(key)) {
      duplicateCount += 1;
      return;
    }

    seen.add(key);
    uniqueIncoming.push(file);
  });

  return {
    combined: [...existingFiles, ...uniqueIncoming],
    uniqueIncoming,
    duplicateCount,
  };
};

const readEntry = (entry) => new Promise((resolve) => {
  if (!entry) {
    resolve([]);
    return;
  }

  if (entry.isFile) {
    entry.file(
      (file) => resolve(isAcceptedResumeFile(file) ? [file] : []),
      () => resolve([]),
    );
    return;
  }

  if (!entry.isDirectory) {
    resolve([]);
    return;
  }

  const reader = entry.createReader();
  const readAllEntries = async () => {
    const chunks = [];

    // Some browsers return directory entries in batches.
    while (true) {
      const batch = await new Promise((batchResolve) => {
        reader.readEntries(
          (entries) => batchResolve(entries || []),
          () => batchResolve([]),
        );
      });

      if (batch.length === 0) break;
      chunks.push(...batch);
    }

    const nested = await Promise.all(chunks.map(readEntry));
    resolve(nested.flat());
  };

  void readAllEntries();
});

export const extractDroppedResumeFiles = async (dataTransfer) => {
  if (!dataTransfer) return [];

  const items = Array.from(dataTransfer.items || []);
  if (items.length > 0 && items.some((item) => typeof item.webkitGetAsEntry === 'function')) {
    const entries = items
      .map((item) => item.webkitGetAsEntry())
      .filter(Boolean);

    if (entries.length > 0) {
      const nested = await Promise.all(entries.map(readEntry));
      return nested.flat();
    }
  }

  return Array.from(dataTransfer.files || []).filter(isAcceptedResumeFile);
};
